import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Clock, ArrowRight, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import api, { mensajeDeError } from '../lib/api';
import { toast } from 'sonner';

/**
 * Cambiar la hora de una sesión ya agendada.
 *
 * Mantiene la misma reserva y el mismo pago: solo mueve el horario. Por eso es
 * una pantalla aparte y no el flujo de agendar, que crearía una reserva nueva
 * y cobraría de nuevo.
 */
const ReprogramarPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { isPatient } = useAuth();

  const [reserva, setReserva] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [dias, setDias] = useState([]);
  const [diaElegido, setDiaElegido] = useState(null);
  const [horarios, setHorarios] = useState([]);
  const [cargandoHorarios, setCargandoHorarios] = useState(false);
  const [horaElegida, setHoraElegida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);

  // Se busca la reserva entre las del paciente: así el servidor ya valida que
  // sea suya, sin exponer un endpoint que reciba cualquier id.
  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/mi/reservas', { params: { scope: 'upcoming' } });
      const encontrada = data.find((r) => r.id === bookingId);

      if (!encontrada) {
        toast.error('No encontramos esa hora entre tus reservas');
        navigate('/mis-horas');
        return;
      }

      setReserva(encontrada);

      const { data: resumen } = await api.get(
        `/profesionales/${encontrada.psychologist_id}/disponibilidad/resumen`,
        { params: { from: format(new Date(), 'yyyy-MM-dd'), days: 30 } }
      );
      setDias(resumen);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/mis-horas');
        return;
      }
      toast.error(mensajeDeError(err, 'No pudimos cargar tu reserva'));
    } finally {
      setCargando(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    if (isPatient) {
      cargar();
    } else {
      setCargando(false);
    }
  }, [isPatient, cargar]);

  useEffect(() => {
    if (!diaElegido || !reserva) return;

    setCargandoHorarios(true);
    setHoraElegida('');

    api
      .get(`/profesionales/${reserva.psychologist_id}/disponibilidad`, {
        params: { date: diaElegido },
      })
      .then(({ data }) => setHorarios(data.slots || []))
      .catch((err) => {
        setHorarios([]);
        toast.error(mensajeDeError(err, 'No pudimos cargar los horarios'));
      })
      .finally(() => setCargandoHorarios(false));
  }, [diaElegido, reserva]);

  const confirmar = async () => {
    setGuardando(true);
    try {
      // Fecha y hora de Chile, tal como se muestran en la grilla. Convertirlas
      // a UTC en el navegador movía la sesión de hora para quien reprogramara
      // desde otro huso.
      await api.patch(`/reservas/${bookingId}/reprogramar`, {
        date: diaElegido,
        time: horaElegida,
      });

      setListo(true);
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos cambiar tu hora'));
    } finally {
      setGuardando(false);
    }
  };

  if (!isPatient && !cargando) {
    return (
      <Marco>
        <div className="text-center">
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
            Ingresa con tu código
          </h1>
          <p className="text-slate-600 mb-6">
            Para cambiar una hora necesitas entrar con tu código de continuidad.
          </p>
          <Link to="/mis-horas">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
              Ir a mis horas
            </Button>
          </Link>
        </div>
      </Marco>
    );
  }

  if (cargando) {
    return (
      <Marco>
        <p className="text-slate-500 text-center">Cargando…</p>
      </Marco>
    );
  }

  if (listo) {
    return (
      <Marco>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
            <Check className="w-8 h-8 text-teal-700" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-3">
            Tu hora quedó cambiada
          </h1>
          <p className="text-slate-600 mb-2 first-letter:uppercase">
            {format(parseISO(`${diaElegido}T00:00:00`), "EEEE d 'de' MMMM", { locale: es })} a las{' '}
            {horaElegida} hrs
          </p>
          <p className="text-sm text-slate-500 mb-8">
            No tienes que pagar de nuevo: es la misma sesión.
          </p>
          <Link to="/mis-horas">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
              Ver mis horas
            </Button>
          </Link>
        </div>
      </Marco>
    );
  }

  const actual = reserva?.scheduled_date ? parseISO(reserva.scheduled_date) : null;
  const conCupo = dias.filter((d) => d.available_slots > 0);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold text-slate-900 mb-2">
            Cambiar la hora
          </h1>
          <p className="text-slate-600">
            Con {reserva?.psychologist_name}. Es la misma sesión y el mismo pago.
          </p>
        </div>

        {actual && (
          <Card className="border-slate-200 mb-6">
            <CardContent className="py-4 flex flex-wrap items-center gap-3">
              <Badge className="bg-slate-100 text-slate-700 border-0">Hora actual</Badge>
              <span className="text-slate-700 first-letter:uppercase">
                {format(actual, "EEEE d 'de' MMMM, HH:mm", { locale: es })} hrs
              </span>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardContent className="py-6">
            <p className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-teal-600" />
              Elige el nuevo día
            </p>

            {conCupo.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">
                  Este profesional no tiene horas disponibles en los próximos 30 días.
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Puedes cancelar esta hora y agendar con otro profesional.
                </p>
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {conCupo.map((dia) => {
                  const fecha = parseISO(`${dia.date}T00:00:00`);
                  const activo = diaElegido === dia.date;

                  return (
                    <button
                      key={dia.date}
                      type="button"
                      onClick={() => setDiaElegido(dia.date)}
                      className={`flex-none w-20 py-3 rounded-xl border text-center transition-colors ${
                        activo
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : 'bg-white border-slate-200 hover:border-teal-300 text-slate-700'
                      }`}
                    >
                      <span className="block text-xs first-letter:uppercase opacity-80">
                        {format(fecha, 'EEE', { locale: es })}
                      </span>
                      <span className="block text-xl font-semibold">{format(fecha, 'd')}</span>
                      <span className="block text-xs first-letter:uppercase opacity-80">
                        {format(fecha, 'MMM', { locale: es })}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {diaElegido && (
          <Card className="mb-6">
            <CardContent className="py-6">
              <p className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-600" />
                Elige la hora
              </p>

              {cargandoHorarios && <p className="text-slate-500 text-sm">Cargando…</p>}

              {!cargandoHorarios && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {horarios.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={horaElegida === slot.time ? 'default' : 'outline'}
                      disabled={!slot.available}
                      onClick={() => setHoraElegida(slot.time)}
                      className={
                        horaElegida === slot.time ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''
                      }
                    >
                      {slot.time}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={confirmar}
            disabled={!horaElegida || guardando}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 disabled:opacity-50"
          >
            {guardando ? 'Cambiando…' : 'Confirmar el cambio'}
            {!guardando && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
          <Link to="/mis-horas" className="sm:w-auto">
            <Button variant="outline" className="w-full rounded-full py-6 px-8">
              Volver
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const Marco = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-lg">{children}</div>
  </div>
);

export default ReprogramarPage;
